<template>
  <input 
    type="number" 
    :value="value"
    @input="handleInput"
    @blur="handleBlur"
    class="numeric-input"
    min="1" 
    max="4"
    :title="`Value must be between 1 and 4 (current: ${value})`"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';

// Props
interface Props {
  value: number;
}

const props = defineProps<Props>();

// Emits
interface Emits {
  'update': [value: number];
}

const emit = defineEmits<Emits>();

// Computed
const isValidValue = computed(() => {
  return props.value >= 1 && props.value <= 4;
});

// Methods
const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value);
  
  if (!isNaN(value) && value >= 1 && value <= 4) {
    emit('update', value);
  }
};

const handleBlur = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value);
  
  // Reset to valid value if invalid
  if (isNaN(value) || value < 1 || value > 4) {
    target.value = String(props.value);
  }
};
</script>

<style scoped>
.numeric-input {
  width: 60px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  text-align: center;
  background-color: white;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.numeric-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.numeric-input:invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

/* Remove spinner buttons for webkit browsers */
.numeric-input::-webkit-outer-spin-button,
.numeric-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Remove spinner buttons for Firefox */
.numeric-input[type=number] {
  -moz-appearance: textfield;
}
</style>
